/**
 * Tiny English pluralization helper for count-based UI strings.
 *
 * Audit follow-ups F2.83 / F2.20 / F2.58 — multiple surfaces rendered
 * "Showing 1 cases", "1 items need review", "1 files need attention"
 * because callers hard-coded the plural noun. This helper resolves the
 * count to the right form so callers do not have to remember to ternary
 * each time.
 *
 * Usage
 * -----
 *   `Showing ${pluralize(n, 'case')}`           → "Showing 1 case" / "Showing 5 cases"
 *   `${pluralize(n, 'item', 'items')} to review` → "1 item to review" / "3 items to review"
 *
 * Notes
 * -----
 * - Returns `<count> <noun>` so callers don't double-render the count.
 * - Pass an explicit `plural` argument when the noun isn't a regular
 *   `<base> + 's'` form (e.g. "child" / "children", "person" / "people").
 * - For more complex i18n (gendered/locale-specific plural rules), reach
 *   for `Intl.PluralRules`. This helper covers English admin-UI strings
 *   only by design — keep it ~15 lines.
 */
export function pluralize(
  count: number,
  singular: string,
  plural?: string,
): string {
  const noun = count === 1 ? singular : (plural ?? `${singular}s`);
  return `${count} ${noun}`;
}

/**
 * Returns just the noun form (without the count). Useful when the count
 * is rendered separately (e.g. in a heading number that uses different
 * styling) but the noun still needs subject-verb agreement.
 *
 *   `${count}` <strong>{pluralizeWord(count, 'case')}</strong>
 */
export function pluralizeWord(
  count: number,
  singular: string,
  plural?: string,
): string {
  return count === 1 ? singular : (plural ?? `${singular}s`);
}
